import React, { useEffect, useRef, useState } from "react"
import Checkbox from '../Checkbox/Checkbox'
import Paragraph from '../Typography/Paragraph/Paragraph'
import Button from '../Button/Button'
import Tooltip from "../Tooltip/Tooltip"

export interface DetectionItem {
    name?: string;
    text?: string;
    color?: string;
    confidence?: number;
    pixel_percentage?: number;
    image?: string;
    bounding_box?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    selected?: boolean;
}

interface PartialSelectResult {
    name?: string;
    selected?: boolean;
}

type UpdateValueFunction = (event: {
    target: {
        value: DetectionItem[];
        type: string;
        name: string;
    };
}) => void;

interface DetectionListItemProps {
    item: DetectionItem;
    showImage?: boolean;
    checkbox?: boolean;
    thresholdValue?: number;
    threshold?: boolean;
    mainImage?: string;
    handleCheckboxResult?: () => void;
    setPartialSelect?: (result: PartialSelectResult) => void;
    showConfidence?: boolean;
    hasHotspots?: boolean;
    canvasSize?: number;
    strokeColor?: string;
}

interface VisualElementProps {
    hasImage: boolean;
    hasColor: boolean;
    item: DetectionItem;
    mainImage?: string;
    boundingBox?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    hasHotspots?: boolean;
    canvasSize?: number;
    strokeColor?: string;
}

export type TranslationPopoverProps = {
    isOpen: boolean
    handleClose: () => void
    popoverButtonRef: React.RefObject<HTMLElement>
    popoverBoundaryRef: React.RefObject<HTMLElement>
    updateValue: UpdateValueFunction
    handleInsert: () => void
}

interface TranslationButtonProps {
    uid: string;
    translationLabel: string;
    TranslationPopover: (props: TranslationPopoverProps) => React.JSX.Element;
    updateValue: UpdateValueFunction;
    masterLanguage: any;
    isTranslated?: boolean;
    setIsTranslated?: (isTranslated: boolean) => void;
}

const VisualElement = ({ hasImage, hasColor, item, mainImage, boundingBox, hasHotspots, canvasSize, strokeColor }: VisualElementProps) => {
    const [imageLoadError, setImageLoadError] = useState<boolean>(false);
    const displayCanvasRef = useRef<HTMLCanvasElement | null>(null);

    // Optimized single effect for image rendering
    useEffect(() => {
        if (!displayCanvasRef.current || !mainImage || !boundingBox || imageLoadError) {
            return;
        }

        const canvas = displayCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Setup canvas with high DPI support
        const devicePixelRatio = window.devicePixelRatio || 1;
        const canvasSizeValue = canvasSize || 40;
        const scaleFactor = Math.max(devicePixelRatio, 2);
        
        canvas.width = canvasSizeValue * scaleFactor;
        canvas.height = canvasSizeValue * scaleFactor;
        ctx.scale(scaleFactor, scaleFactor);
        canvas.style.width = canvasSizeValue + 'px';
        canvas.style.height = canvasSizeValue + 'px';

        const img = new Image();
        
        img.onload = () => {
            ctx.clearRect(0, 0, canvasSizeValue, canvasSizeValue);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Calculate if we should zoom or show full image
            const maxDimension = Math.max(boundingBox.width, boundingBox.height);
            const cropPadding = hasHotspots ? maxDimension * 0.15 : 0;
            const paddedDimension = maxDimension + (2 * cropPadding);
            const shouldShowFullImage = paddedDimension > Math.min(img.width, img.height) * 0.8;
            
            if (shouldShowFullImage) {
                // Show full image with bounding box overlay
                const { drawWidth, drawHeight, offsetX, offsetY } = calculateImageFit(canvasSizeValue, canvasSizeValue, img.width, img.height);
                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                
                if (hasHotspots && strokeColor) {
                    drawBoundingBox(ctx, boundingBox, offsetX, offsetY, drawWidth, drawHeight, img.width, img.height, canvasSizeValue, strokeColor);
                }
            } else {
                // Zoom/crop behavior for smaller boxes
                const centerX = boundingBox.x + boundingBox.width / 2;
                const centerY = boundingBox.y + boundingBox.height / 2;
                const squareCropX = centerX - paddedDimension / 2;
                const squareCropY = centerY - paddedDimension / 2;
                
                const finalCropX = Math.max(0, Math.min(squareCropX, img.width - paddedDimension));
                const finalCropY = Math.max(0, Math.min(squareCropY, img.height - paddedDimension));
                const finalCropSize = Math.min(paddedDimension, Math.min(img.width - finalCropX, img.height - finalCropY));
                
                ctx.drawImage(img, finalCropX, finalCropY, finalCropSize, finalCropSize, 0, 0, canvasSizeValue, canvasSizeValue);
                
                if (hasHotspots && strokeColor) {
                    drawCroppedBoundingBox(ctx, boundingBox, finalCropX, finalCropY, finalCropSize, canvasSizeValue, strokeColor);
                }
            }
        };
        
        img.onerror = () => {
            console.error('Failed to load image');
            setImageLoadError(true);
        };
        
        img.src = mainImage;
    }, [mainImage, boundingBox, hasHotspots, strokeColor, imageLoadError]);

    // Helper function to calculate image fit
    const calculateImageFit = (canvasWidth: number, canvasHeight: number, imgWidth: number, imgHeight: number) => {
        const imgAspect = imgWidth / imgHeight;
        const canvasAspect = canvasWidth / canvasHeight;
        
        if (imgAspect > canvasAspect) {
            return {
                drawWidth: canvasWidth,
                drawHeight: canvasWidth / imgAspect,
                offsetX: 0,
                offsetY: (canvasHeight - canvasWidth / imgAspect) / 2
            };
        } else {
            return {
                drawWidth: canvasHeight * imgAspect,
                drawHeight: canvasHeight,
                offsetX: (canvasWidth - canvasHeight * imgAspect) / 2,
                offsetY: 0
            };
        }
    };

    // Helper function to draw bounding box on full image
    const drawBoundingBox = (ctx: CanvasRenderingContext2D, boundingBox: any, offsetX: number, offsetY: number, drawWidth: number, drawHeight: number, imgWidth: number, imgHeight: number, canvasWidth: number, strokeColor: string) => {
        const scaleX = drawWidth / imgWidth;
        const scaleY = drawHeight / imgHeight;
        
        const boxX = offsetX + (boundingBox.x * scaleX);
        const boxY = offsetY + (boundingBox.y * scaleY);
        const boxWidth = boundingBox.width * scaleX;
        const boxHeight = boundingBox.height * scaleY;
        
        const minDimension = Math.min(boxWidth, boxHeight);
        const radius = Math.max(2, Math.min(minDimension * 0.1, 8));
        
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = Math.max(2, canvasWidth * 0.03);
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, radius);
        ctx.stroke();
    };

    // Helper function to draw bounding box on cropped image
    const drawCroppedBoundingBox = (ctx: CanvasRenderingContext2D, boundingBox: any, finalCropX: number, finalCropY: number, finalCropSize: number, canvasWidth: number, strokeColor: string) => {
        const relativeX = boundingBox.x - finalCropX;
        const relativeY = boundingBox.y - finalCropY;
        
        const intersectsLeft = relativeX < finalCropSize;
        const intersectsTop = relativeY < finalCropSize;
        const intersectsRight = relativeX + boundingBox.width > 0;
        const intersectsBottom = relativeY + boundingBox.height > 0;
        
        if (intersectsLeft && intersectsTop && intersectsRight && intersectsBottom) {
            const intersectX = Math.max(0, relativeX);
            const intersectY = Math.max(0, relativeY);
            const intersectRight = Math.min(finalCropSize, relativeX + boundingBox.width);
            const intersectBottom = Math.min(finalCropSize, relativeY + boundingBox.height);
            const intersectWidth = intersectRight - intersectX;
            const intersectHeight = intersectBottom - intersectY;
            
            const canvasScale = canvasWidth / finalCropSize;
            const canvasX = intersectX * canvasScale;
            const canvasY = intersectY * canvasScale;
            const canvasBoxWidth = intersectWidth * canvasScale;
            const canvasBoxHeight = intersectHeight * canvasScale;
            
            const minDimension = Math.min(canvasBoxWidth, canvasBoxHeight);
            const radius = Math.max(2, Math.min(minDimension * 0.1, 8));
            
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = Math.max(2, canvasWidth * 0.03);
            ctx.beginPath();
            ctx.roundRect(canvasX, canvasY, canvasBoxWidth, canvasBoxHeight, radius);
            ctx.stroke();
        }
    };


    if (hasImage && mainImage && boundingBox) {
        return (
            <div className='Sidebar__list-image-container' style={{ width: canvasSize || 40, height: canvasSize || 40 }}>
                {imageLoadError ? (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#999',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    >
                        Image
                    </div>
                ) : (
                    <canvas
                        ref={displayCanvasRef}
                        width={canvasSize || 40}
                        height={canvasSize || 40}
                        style={{
                            backgroundColor: '#f5f5f5'
                        }}
                    />
                )}
            </div>
        );
    }

    if (hasImage && item.image) {
        return (
            <div className='Sidebar__list-image-container' style={{ width: canvasSize || 40, height: canvasSize || 40 }}>
                {imageLoadError ? (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#999',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    >
                        Image
                    </div>
                ) : (
                    <canvas
                        ref={displayCanvasRef}
                        width={canvasSize || 40}
                        height={canvasSize || 40}
                        style={{
                            backgroundImage: item.image ? `url(${item.image})` : 'none',
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: item.image ? 'transparent' : '#f5f5f5'
                        }}
                    />
                )}
            </div>
        );
    }

    if (hasColor) {
        return (
            <div className='Sidebar__list-color__palette-container'>
                <div
                    className='Sidebar__list-color__palette'
                    style={{ backgroundColor: item.color }}
                />
            </div>
        );
    }

    return null;
}


export const DetectionListItem = ({
    item,
    showImage = true,
    checkbox = false,
    thresholdValue = 0,
    threshold,
    mainImage,
    handleCheckboxResult,
    setPartialSelect,
    showConfidence,
    hasHotspots,
    canvasSize,
    strokeColor
}: DetectionListItemProps) => {
    const displayText = item.name || item.color || item.text || '';
    const displayConfidence = item.confidence || item.pixel_percentage || 0;
    const hasImage = showImage && (Boolean(item.image) || Boolean(item.bounding_box));
    const hasColor = Boolean(item.color);

    // Check if item is below threshold
    const belowThreshold = item.confidence
        ? item.confidence < thresholdValue
        : item.pixel_percentage
            ? item.pixel_percentage < thresholdValue
            : false;

    const handleCheckboxChange = () => {
        item.selected = !item.selected;
        setPartialSelect?.({ name: item.name, selected: item.selected });
        handleCheckboxResult?.();
    };

    return (
        <div className='Sidebar__listitem__container'>
            <div className={`Sidebar__listitem ${hasImage ? 'Sidebar__list-image' : ''}`}>
                <div className='detection__container'>
                    {checkbox && (
                        <div className='Sidebar__checkbox-container'>
                            <Checkbox
                                checked={item.selected || false}
                                onChange={handleCheckboxChange}
                                className='Sidebar__checkbox'
                            />
                        </div>
                    )}

                    <VisualElement
                        hasImage={hasImage}
                        hasColor={hasColor}
                        item={item}
                        mainImage={mainImage}
                        boundingBox={item.bounding_box}
                        hasHotspots={hasHotspots}
                        canvasSize={canvasSize}
                        strokeColor={strokeColor}
                    />

                    <Paragraph
                        text={displayText}
                        variant='p2'
                        variantStyle='regular'
                        className='Sidebar__listlabel'
                    />
                </div>

                {showConfidence && <Tooltip
                    content={item.pixel_percentage ? `Pixel Percentage` : `Confidence Score`}
                    position='top'
                    version='v2'
                    className='Badge__tooltip'
                    showArrow={true}
                    wrapperElementType="span"
                    style={{ paddingTop: 0 }}
                >
                    <div className={`Sidebar__badge ${threshold && belowThreshold ? 'below__threshold' : ''}`}>
                        {item.pixel_percentage ? displayConfidence.toFixed(2) : displayConfidence}%
                    </div>
                </Tooltip>}
            </div>
        </div>
    )
}

export const TranslationButton = ({
    uid,
    translationLabel,
    TranslationPopover,
    updateValue,
    masterLanguage,
    isTranslated,
    setIsTranslated
}: TranslationButtonProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverButtonRef = useRef(null);
    const popoverBoundaryRef = useRef(null);

    const handleClose = () => {
        setIsOpen(false);
    }

    const handleOpen = () => {
        setIsOpen(true);
    }

    const handleInsert = () => {
        setIsTranslated?.(true);
    }

    const handleRevert = () => {
        updateValue({
            target: {
                value: masterLanguage,
                type: "change",
                name: uid
            }
        });
        setIsTranslated?.(false);
    }

    return (
        <div className='Sidebar__text-area__translation-container' ref={popoverBoundaryRef}>
            <Button
                buttonType='tertiary'
                size='small'
                className='Sidebar__text-area__translation-button'
                buttonRef={popoverButtonRef}
                onClick={isTranslated ? () => handleRevert() : handleOpen}
                version='v2'
                icon={'v2-Language'}
            >
                {isTranslated ? "Revert to Master Language" : translationLabel || "Translate"}
            </Button>

            <TranslationPopover
                isOpen={isOpen}
                handleClose={handleClose}
                popoverButtonRef={popoverButtonRef}
                popoverBoundaryRef={popoverBoundaryRef}
                updateValue={updateValue}
                handleInsert={handleInsert}
            />
        </div>
    )
}